import React from 'react';
import { MaskedImage, Hero, Layout, SectionZone, RichText, SEO } from '@/components';
import { usePreferredLanguage } from '@/hooks';
import { GlobalSettingsInterface, LanguageCodeType, SiteBannerInterface } from '@/types';
import type { PortableTextBlock as PortableTextBlockType, ImageAsset } from '@sanity/types';
import { portabletextToText } from '@/utils';
import Cookies from 'universal-cookie';

interface ImageWithMask {
  alt: string;
  image: {
    asset: ImageAsset;
  };
  mask?: string;
}

interface ExhibitProps {
  pageContext: {
    language: string;
    languageVersions: { language: LanguageCodeType; href: string }[];
    languages: LanguageCodeType[];
    title: string;
    blurb: PortableTextBlockType;
    content?: PortableTextBlockType;
    attributionRecognition?: string;
    photo?: ImageWithMask;
    featuredImage: ImageWithMask;
    sections?: any[];
    pathname?: string;
    globalSettings: GlobalSettingsInterface[];
    siteBanner: SiteBannerInterface[];
  };
}

export default function Exhibit({ pageContext }: ExhibitProps) {
  const {
    language,
    languageVersions,
    languages,
    title,
    blurb,
    content,
    attributionRecognition,
    featuredImage,
    photo,
    sections = [],
    globalSettings,
    siteBanner,
  } = pageContext || {};

  usePreferredLanguage({ language, languageVersions });

  const cookies = new Cookies();
  const preferredLanguage = cookies.get('preferredLanguage') || 'en';

  const cta = {
    label: 'get tickets',
    internalLink: {
      slug: { current: 'tickets' },
      language: preferredLanguage,
      _type: 'page',
    },
    type: 'primary',
  };

  const heroImage = {
    ...featuredImage,
    mask: 'blob-wide',
  };

  return (
    <Layout
      languageVersions={languageVersions}
      currentLanguage={language}
      languages={languages}
      globalSettings={globalSettings}
      siteBanner={siteBanner}
    >
      <Hero
        backgroundColor={{ label: 'green-500', value: '#A0DAB3' }}
        heading={title}
        includeSubheading={false}
        blurb={blurb}
        includeSquiggle={true}
        isBiggerHero={false}
        heroImage={heroImage}
        includeCta={true}
        backgroundElementLeft='half_circles_left'
        doodadLayout='layout5'
        cta={cta}
        blurbClassName='line-clamp-2'
        isCompactHero={false}
        isSearchPageHero={false}
        includeCtas={false}
      />

      <section className='relative'>
        <div className='relative z-10 mx-auto flex max-w-screen-xl flex-col items-center md:flex-row'>
          <div className='bg-sand-500 w-full md:w-7/12 xl:w-[820px]'>
            <div className='mx-auto flex max-w-screen-xl flex-col px-5 py-8 pb-3 md:p-[50px]'>
              <div className='mb-4'>
                <RichText
                  value={blurb}
                  className='text-2xl font-semibold'
                />
              </div>
              {content && (
                <RichText
                  className={`text-xl font-light leading-7 text-black`}
                  value={content}
                />
              )}
              {attributionRecognition && (
                <p className='mt-4 border-t border-black border-opacity-15 px-0 pt-2 text-xl font-normal leading-7 text-black'>
                  {attributionRecognition}
                </p>
              )}
            </div>
          </div>
          <div className='bg-sand-500 w-full md:w-5/12 xl:w-[460px]'>
            <div className='flex h-full w-full items-center'>
              <div className='mx-auto flex max-w-screen-xl flex-col px-5 py-8 pt-3 md:p-[50px]'>
                {photo?.image && (
                  <MaskedImage
                    image={photo.image}
                    alt={photo.alt}
                    mask='circle'
                    className='aspect-square w-full'
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='absolute left-0 top-0 z-0 flex h-full w-full flex-col md:flex-row'>
          <div className={`bg-sand-500 w-full md:w-7/12 xl:w-2/3`}></div>
          <div className={`bg-sand-500 w-full md:w-5/12 xl:w-1/3`}></div>
        </div>
      </section>

      {sections &&
        sections.map((section, index) => (
          <SectionZone
            key={index}
            section={section}
            globalSettings={globalSettings}
          />
        ))}
    </Layout>
  );
}

export const Head = ({ pageContext }: ExhibitProps) => {
  const { title, language, languageVersions, blurb, featuredImage, pathname, globalSettings } =
    pageContext;

  const description = portabletextToText(blurb);

  return (
    <SEO
      title={title}
      description={description}
      image={featuredImage?.image?.asset?.url}
      pathname={pathname}
      language={language}
      languageVersions={languageVersions}
      globalSettings={globalSettings}
    />
  );
};
